// Communications contracts (module 13: PB-13-03..08; PD-045..PD-048).
import { z } from 'zod';

const Uuid = z.uuid();

export const TEMPLATE_KEYS = ['invitation', 'reminder', 'cancellation', 'host_arrival', 'rsvp_reply', 'approval_requested', 'approval_decided', 'parking_change', 'gate_host_contact', 'protocol_notice'] as const;
export type TemplateKeyDto = (typeof TEMPLATE_KEYS)[number];

/** Variables each template may use; anything else is refused so a typo never reaches a recipient. */
export const TEMPLATE_VARIABLES: Readonly<Record<TemplateKeyDto, readonly string[]>> = {
  // PB-04-08 (PD-525, PD-527): logoSrc is the inline site logo (cid: reference, set only when the
  // site has a usable logo); requiredActions is the visitor's outstanding pre-registration steps.
  invitation: ['visitorName', 'hostName', 'siteName', 'title', 'when', 'timeZone', 'destination', 'address', 'instructions', 'contactLine', 'brandColour', 'reference', 'link', 'logoSrc', 'requiredActions'],
  reminder: ['visitorName', 'siteName', 'title', 'when', 'timeZone', 'reference', 'link', 'brandColour'],
  cancellation: ['visitorName', 'siteName', 'title', 'when', 'timeZone', 'reference', 'contactLine', 'brandColour'],
  host_arrival: ['hostName', 'visitorName', 'organisation', 'siteName', 'arrivedAt', 'timeZone', 'title', 'reference', 'brandColour'],
  rsvp_reply: ['hostName', 'visitorName', 'reply', 'title', 'when', 'timeZone', 'reference', 'siteName', 'brandColour'],
  approval_requested: ['recipientName', 'stage', 'title', 'when', 'timeZone', 'hostName', 'reference', 'siteName', 'link', 'brandColour'],
  approval_decided: ['hostName', 'visitorName', 'decision', 'title', 'when', 'timeZone', 'reference', 'siteName', 'link', 'brandColour'],
  parking_change: ['visitorName', 'siteName', 'zone', 'space', 'when', 'timeZone', 'reference', 'brandColour'],
  gate_host_contact: ['hostName', 'visitorName', 'plate', 'gate', 'siteName', 'reference', 'brandColour'],
  // ADR-027 (PD-354): protocol notice to a visitor type's protocol contact and owner; never the itinerary.
  protocol_notice: ['recipientName', 'event', 'title', 'when', 'timeZone', 'hostName', 'reference', 'siteName', 'link', 'brandColour'],
};

export const TemplateListQuery = z.object({ siteId: Uuid.optional() });

export const CreateTemplateRequest = z
  .object({
    siteId: Uuid.nullable(),
    key: z.enum(TEMPLATE_KEYS),
    channel: z.enum(['email', 'sms', 'whatsapp']),
    locale: z.enum(['en', 'ar']),
    subject: z.string().trim().max(200, 'v.tooLong').nullable().optional(),
    bodyText: z.string().trim().min(1, 'v.required').max(5000, 'v.tooLong'),
    bodyHtml: z.string().trim().max(20000, 'v.tooLong').nullable().optional(),
    providerTemplate: z.string().trim().regex(/^[a-z0-9_]{1,120}$/, 'v.providerTemplate').nullable().optional(),
    note: z.string().trim().max(500, 'v.tooLong').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.channel === 'email' && !v.subject) ctx.addIssue({ code: 'custom', path: ['subject'], message: 'v.required' });
    if (v.channel === 'whatsapp' && !v.providerTemplate) ctx.addIssue({ code: 'custom', path: ['providerTemplate'], message: 'v.required' });
    if (v.channel !== 'email' && v.bodyHtml) ctx.addIssue({ code: 'custom', path: ['bodyHtml'], message: 'v.invalid' });
  });

export const ApproveTemplateRequest = z.object({ note: z.string().trim().max(500, 'v.tooLong').nullable().optional() });
export const PreviewTemplateRequest = z.object({
  key: z.enum(TEMPLATE_KEYS),
  channel: z.enum(['email', 'sms', 'whatsapp']),
  locale: z.enum(['en', 'ar']),
  subject: z.string().max(200).nullable().optional(),
  bodyText: z.string().max(5000),
  bodyHtml: z.string().max(20000).nullable().optional(),
});

export interface SmsPreviewDto {
  readonly encoding: 'gsm7' | 'ucs2';
  readonly units: number;
  readonly segments: number;
  readonly perSegment: number;
}

export interface TemplateResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly key: TemplateKeyDto;
  readonly channel: 'email' | 'sms' | 'whatsapp';
  readonly locale: 'en' | 'ar';
  readonly number: number;
  readonly status: 'draft' | 'approved' | 'retired';
  readonly subject: string | null;
  readonly bodyText: string;
  readonly bodyHtml: string | null;
  readonly providerTemplate: string | null;
  readonly note: string | null;
  readonly createdBy: string;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly createdAt: string;
  readonly sms: SmsPreviewDto | null;
}

export interface TemplatePreviewResponse {
  readonly subject: string | null;
  readonly text: string;
  readonly html: string | null;
  readonly sms: SmsPreviewDto | null;
  readonly unknownVariables: readonly string[];
}

export const MessageListQuery = z.object({
  siteId: Uuid,
  visitId: Uuid.optional(),
  status: z.enum(['queued', 'accepted', 'failed', 'blocked', 'refused', 'unknown']).optional(), // refused/unknown: PD-584
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface MessageLogItem {
  readonly id: string;
  readonly visitId: string;
  readonly purpose: string;
  readonly channel: 'email' | 'sms' | 'whatsapp';
  /** Masked: the log proves delivery state, it does not reveal contact details. */
  readonly recipient: string;
  readonly locale: 'en' | 'ar';
  readonly templateVersion: string;
  readonly status: 'queued' | 'accepted' | 'failed' | 'blocked' | 'refused' | 'unknown'; // refused/unknown: PD-584
  readonly statusReason: string | null;
  readonly attempts: number;
  readonly segments: number | null;
  readonly createdAt: string;
  readonly acceptedAt: string | null;
}

export const ConsentRequest = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{20,200}$/, 'v.invalid'),
  channel: z.enum(['sms', 'whatsapp']),
  decision: z.enum(['granted', 'withdrawn']),
});

export interface ConsentView {
  /** Channels the organisation uses for this visit's visitor messages, and the visitor's current choice. */
  readonly channels: ReadonlyArray<{ readonly channel: 'sms' | 'whatsapp'; readonly granted: boolean }>;
}
